import React, { lazy } from 'react'
import { DashSwitch } from '@retailer-platform/dashboard/routing'
import { DateRangePickerPresetOption } from '@retailer-platform/shared-components/src/common/date-range-picker/utils/DateRangePickerPresets.utils'
import { useLocation } from 'react-router'
import { DomainRoute } from '../utils/domain/routingComponents'
import { FilterParamsProvider } from '../utils/filter-params/FilterParamsProvider'
import { insightsPortalAdsDashboardAccessControl } from '../access-control/insightsPortalAdsDashboardAccess.configuration'
import { insightsPortalAccessControl } from '../access-control/insightsPortalAccess.configuration'
import { insightsPortalDataExplorerAccessControl } from '../access-control/insightsPortalDataExplorerAccess.configuration'
import { insightsPortalCampaignInsightsAccessControl } from '../access-control/insightsPortalCampaignInsights.configuration'
import { insightsPortalCollectionsAccessControl } from '../access-control/insightsPortalCollectionInsights.configuration'
import { insightsPortalCreativeInsightsAccessControl } from '../access-control/insightsPortalCreativeInsights.configuration'
import { insightsPortalAwarenessCampaignInsightsAccessControl } from '../access-control/insightsPortalAwarenessCampaignInsights.configuration'
import { InsightsPortalStoreLaborPerformanceAccessControl } from '../access-control/insightsPortalStoreLaborPerformanceAccess.configuration'
import { insightsPortalFlyersAccessControl } from '../access-control/insightsPortalFlyersAccess.configuration'
import { insightsPortalCaperDashboardAccessControl } from '../access-control/insightsPortalCaperDashboardAccess.configuration'
import { insightsPortalCartAssistantDashboardAccessControl } from '../access-control/insightsPortalCartAssistantDashboardAccess.configuration'
import { insightsPortalDeliveryDashboardAccessControl } from '../access-control/insightsPortalDeliveryDashboardAccess.configuration'
import { insightsPortalOfferIngestionAccessControl } from '../access-control/insightsPortalOfferIngestionAccess.configuration'

const InsightsPortalFilterParamsProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const { pathname } = useLocation()
  const isCaperRoute = pathname.includes('/caper-reporting-dashboard')
  return (
    <FilterParamsProvider
      defaultDateRangePreset={
        isCaperRoute
          ? DateRangePickerPresetOption.Last28Days
          : DateRangePickerPresetOption.Last7Days
      }
      defaultToWeeklyAggregation={isCaperRoute}
    >
      {children}
    </FilterParamsProvider>
  )
}

const InsightsPortalAdsDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalAdsDashboardPage" */
      '../pages/ads-dashboard/InsightsPortalAdsDashboardPage'
    )
)

const InsightsPortalSponsoredProductAdsDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalSponsoredProductAdsDashboardPage" */
      '../pages/ads-dashboard/InsightsPortalSponsoredProductAdsDashboardPage'
    )
)
const InsightsPortalDisplayAdsDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalDisplayAdsDashboardPage" */
      '../pages/ads-dashboard/InsightsPortalDisplayAdsDashboardPage'
    )
)
const InsightsPortalPromotionsAdsDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalPromotionsAdsDashboardPage" */
      '../pages/ads-dashboard/InsightsPortalPromotionsAdsDashboardPage'
    )
)

const InsightsPortalStoreLaborPerformanceDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalStoreLaborPerformanceDashboardPage" */
      '../pages/store-labor-performance/InsightsPortalStoreLaborPerformancePage'
    )
)

const InsightsPortalExecutiveSummaryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalExecutiveSummaryPage" */
      '../pages/executive-summary/InsightsPortalExecutiveSummaryPage'
    )
)
const InsightsPortalPlacementsInsightsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalPlacementsInsightsPage" */
      '../pages/placements-insights/InsightsPortalPlacementsInsightsPage'
    )
)

const InsightsPortalCollectionsOverviewDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCompletedOrdersDashboardPage" */
      '../pages/merchandising/InsightsPortalCollectionsOverviewPage'
    )
)
const InsightsPortalCollectionsProductCarouselDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCollectionsProductCarouselDashboardPage" */
      '../pages/merchandising/InsightsPortalCollectionsProductCarouselPage'
    )
)
const InsightsPortalCollectionsSingleCollectionDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCollectionsSingleCollectionDashboardPage" */
      '../pages/merchandising/InsightsPortalCollectionsSingleCollectionPage'
    )
)
const InsightsPortalCollectionsDepartmentsDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCollectionsDepartmentsDashboardPage" */
      '../pages/merchandising/InsightsPortalCollectionsDepartmentsPage'
    )
)
const InsightsPortalCollectionsPageDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCollectionsPageDashboardPage" */
      '../pages/merchandising/InsightsPortalCollectionsPagePage'
    )
)
const InsightsPortalCompletedOrdersDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCompletedOrdersDashboardPage" */
      '../pages/store-performance/InsightsPortalStorePerformanceCompletedOrdersPage'
    )
)
const InsightsPortalStorePerformanceBasketDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalStorePerformanceBasketDashboardPage" */
      '../pages/store-performance/InsightsPortalStorePerformanceBasketOverviewPage'
    )
)
const InsightsPortalStorePerformanceBasketOutOfStockDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalStorePerformanceBasketOutOfStockDashboardPage" */
      '../pages/store-performance/InsightsPortalStorePerformanceBasketOutOfStockPage'
    )
)
const InsightsPortalStorePerformanceSearchDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalStorePerformanceSearchDashboardPage" */
      '../pages/store-performance/InsightsPortalStorePerformanceSearchDashboardPage'
    )
)
const InsightsPortalMarketingCampaignsAnalyticsOverviewDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingCampaignsAnalyticsOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingCampaignsAnalyticsOverviewPage'
    )
)
const InsightsPortalMarketingCampaignsAnalyticsRedemptionsInAppDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingCampaignsAnalyticsOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingCampaignsAnalyticsRedemptionInAppPage'
    )
)
const InsightsPortalMarketingCampaignsAnalyticsBasketImpactDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingCampaignsAnalyticsOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingCampaignsAnalyticsBasketImpactPage'
    )
)
const InsightsPortalMarketingCampaignsAnalyticsUserGrowthDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingCampaignsAnalyticsOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingCampaignsAnalyticsUserGrowthPage'
    )
)
const InsightsPortalStorePerformanceImageCoverageDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalStorePerformanceImageCoverageDashboardPage" */
      '../pages/store-performance/InsightsPortalStorePerformanceImageCoveragePage'
    )
)
const InsightsPortalReportsLogsCustomReportListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalReportsLogsCustomReportListPage" */
      '../pages/reports-logs/InsightsPortalReportsLogsCustomReportListPage'
    )
)
const InsightsPortalReportsLogsCustomReportExportsListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalReportsLogsCustomReportExportsListPage" */
      '../pages/reports-logs/InsightsPortalReportsLogsCustomReportExportsListPage'
    )
)
const InsightsPortalReportsLogsCustomReportPreviewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalReportsLogsCustomReportPreviewPage" */
      '../pages/reports-logs/InsightsPortalReportsLogsCustomReportPreviewPage'
    )
)
const InsightsPortalMarketingAwarenessCampaignsAnalyticsOverviewDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingAwarenessCampaignsAnalyticsOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingAwarenessCampaignAnalyticsOverviewPage'
    )
)
const InsightsPortalMarketingAwarenessCampaignsAnalyticsBasketImpactDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingAwarenessCampaignsAnalyticsOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingAwarenessCampaignAnalyticsBasketImpactPage'
    )
)
const InsightsPortalMarketingAwarenessCampaignsAnalyticsFunnelDataDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingAwarenessCampaignsAnalyticsOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingAwarenessCampaignAnalyticsFunnelDataPage'
    )
)

const InsightsPortalDisplayPreviewBlockingToolPage = lazy(
  () =>
    import('../pages/display-preview-blocking-tool/InsightsPortalDisplayPreviewBlockingToolPage')
)

const InsightsPortalMarketingFlyersOverviewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingFlyersOverviewPage" */
      '../pages/marketing/InsightsPortalMarketingFlyersOverviewPage'
    )
)

const InsightsPortalmarketingFlyersSingleFlyerPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalMarketingFlyersSingleFlyerPage" */
      '../pages/marketing/InsightsPortalMarketingFlyersSingleFlyerPage'
    )
)

const InsightsPortalCartAssistantDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCartAssistantDashboardPage" */
      '../pages/cart-assistant-dashboard/InsightsPortalCartAssistantDashboardPage'
    )
)

const InsightsPortalCaperReportingDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalCaperReportingDashboardPage" */
      '../pages/caper-reporting-dashboard/CaperReportingDashboardPage'
    )
)

const CaperDashboardOverviewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardOverviewPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardOverviewPage'
    )
)

const CaperDashboardSessionsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardSessionsPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardSessionsPage'
    )
)

const CaperDashboardSessionTrendsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardSessionTrendsPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardSessionTrendsPage'
    )
)

const CaperDashboardStoreTrendPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardStoreTrendPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardStoreTrendPage'
    )
)

const CaperDashboardAuditsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardAuditsPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardAuditsPage'
    )
)

const CaperDashboardLoyaltyPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardLoyaltyPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardLoyaltyPage'
    )
)

const CaperDashboardOperationsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardOperationsPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardOperationsPage'
    )
)

const CaperDashboardStorePerformancePage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardStorePerformancePage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardStorePerformancePage'
    )
)

const CaperDashboardSessionDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CaperDashboardSessionDetailsPage" */
      '../pages/caper-reporting-dashboard/tabs/CaperDashboardSessionDetailsPage'
    )
)
const InsightsPortalDeliveryPerformancePage = lazy(
  () => import('../pages/delivery-performance/InsightsPortalDeliveryPerformancePage')
)
const InsightsPortalOfferIngestionPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InsightsPortalOfferIngestionPage" */
      '../pages/offer-ingestion/InsightsPortalOfferIngestionPage'
    )
)
const OfferIngestionSummaryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "OfferIngestionSummaryPage" */
      '../pages/offer-ingestion/tabs/OfferIngestionSummaryPage'
    )
)
const OfferIngestionSearchPage = lazy(
  () =>
    import(
      /* webpackChunkName: "OfferIngestionSearchPage" */
      '../pages/offer-ingestion/tabs/OfferIngestionSearchPage'
    )
)
const OfferIngestionSupportedOffersPage = lazy(
  () =>
    import(
      /* webpackChunkName: "OfferIngestionSupportedOffersPage" */
      '../pages/offer-ingestion/tabs/OfferIngestionSupportedOffersPage'
    )
)
const DeliveryPerformanceOverviewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DeliveryPerformanceOverviewPage" */
      '../pages/delivery-performance/tabs/DeliveryPerformanceOverviewPage'
    )
)
const DeliveryPerformanceDeliveryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DeliveryPerformanceDeliveryPage" */
      '../pages/delivery-performance/tabs/DeliveryPerformanceDeliveryPage'
    )
)
// @hygen:inject page-import

export const insightsPortalRouter = (
  <DomainRoute route="insights-portal">
    <InsightsPortalFilterParamsProvider>
      <DashSwitch>
        <DomainRoute
          exact
          route="insights-portal-store-performance-completed-orders"
          tool="completed-orders-dashboard-tool"
          component={InsightsPortalCompletedOrdersDashboardPage}
          accessControl={insightsPortalAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-store-performance-basket-overview"
          tool="basket-dashboard-tool"
          component={InsightsPortalStorePerformanceBasketDashboardPage}
          accessControl={insightsPortalAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-store-performance-basket-out-of-stock"
          tool="basket-dashboard-tool"
          component={InsightsPortalStorePerformanceBasketOutOfStockDashboardPage}
          accessControl={insightsPortalAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-store-performance-search"
          tool="search-dashboard-dashboard-tool"
          component={InsightsPortalStorePerformanceSearchDashboardPage}
          accessControl={insightsPortalAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-store-performance-image-coverage"
          tool="image-coverage-dashboard-dashboard-tool"
          component={InsightsPortalStorePerformanceImageCoverageDashboardPage}
          accessControl={insightsPortalAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-merchandising-collections-overview"
          tool="insights-portal-collections-dashboard-tool"
          component={InsightsPortalCollectionsOverviewDashboardPage}
          accessControl={insightsPortalCollectionsAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-merchandising-collections-product-carousel"
          tool="insights-portal-collections-dashboard-tool"
          component={InsightsPortalCollectionsProductCarouselDashboardPage}
          accessControl={insightsPortalCollectionsAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-merchandising-collections-single-collection"
          tool="insights-portal-collections-dashboard-tool"
          component={InsightsPortalCollectionsSingleCollectionDashboardPage}
          accessControl={insightsPortalCollectionsAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-merchandising-collections-departments"
          tool="insights-portal-collections-dashboard-tool"
          component={InsightsPortalCollectionsDepartmentsDashboardPage}
          accessControl={insightsPortalCollectionsAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-merchandising-collections-page"
          tool="insights-portal-collections-dashboard-tool"
          component={InsightsPortalCollectionsPageDashboardPage}
          accessControl={insightsPortalCollectionsAccessControl}
        />
        <DomainRoute
          route="insights-portal-marketing-campaigns-analytics-overview"
          tool="campaigns-with-offers-dashboard-tool"
          component={InsightsPortalMarketingCampaignsAnalyticsOverviewDashboardPage}
          accessControl={insightsPortalCampaignInsightsAccessControl}
        />
        <DomainRoute
          route="insights-portal-marketing-campaigns-analytics-redemptions-in-app"
          tool="campaigns-with-offers-dashboard-tool"
          component={InsightsPortalMarketingCampaignsAnalyticsRedemptionsInAppDashboardPage}
          accessControl={insightsPortalCampaignInsightsAccessControl}
        />
        <DomainRoute
          route="insights-portal-marketing-campaigns-analytics-user-growth"
          tool="campaigns-with-offers-dashboard-tool"
          component={InsightsPortalMarketingCampaignsAnalyticsUserGrowthDashboardPage}
          accessControl={insightsPortalCampaignInsightsAccessControl}
        />
        <DomainRoute
          route="insights-portal-marketing-campaigns-analytics-basket-impact"
          tool="campaigns-with-offers-dashboard-tool"
          component={InsightsPortalMarketingCampaignsAnalyticsBasketImpactDashboardPage}
          accessControl={insightsPortalCampaignInsightsAccessControl}
        />
        <DomainRoute
          route="insights-portal-marketing-awareness-campaigns-analytics-overview"
          tool="campaigns-without-offers-dashboard-tool"
          component={InsightsPortalMarketingAwarenessCampaignsAnalyticsOverviewDashboardPage}
          accessControl={insightsPortalAwarenessCampaignInsightsAccessControl}
        />
        <DomainRoute
          route="insights-portal-marketing-awareness-campaigns-analytics-basket-impact"
          tool="campaigns-without-offers-dashboard-tool"
          component={InsightsPortalMarketingAwarenessCampaignsAnalyticsBasketImpactDashboardPage}
          accessControl={insightsPortalAwarenessCampaignInsightsAccessControl}
        />
        <DomainRoute
          route="insights-portal-marketing-awareness-campaigns-analytics-funnel-data"
          tool="campaigns-without-offers-dashboard-tool"
          component={InsightsPortalMarketingAwarenessCampaignsAnalyticsFunnelDataDashboardPage}
          accessControl={insightsPortalAwarenessCampaignInsightsAccessControl}
        />
        <DomainRoute
          route="insights-portal-placements-insights"
          tool="creatives-dashboard-dashboard-tool"
          component={InsightsPortalPlacementsInsightsPage}
          accessControl={insightsPortalCreativeInsightsAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-reports-logs-custom-report-list"
          tool="custom-reports-dashboard-tool"
          component={InsightsPortalReportsLogsCustomReportListPage}
          accessControl={insightsPortalDataExplorerAccessControl}
          scopePicker={{
            hiddenScopePicker: true,
          }}
        />
        <DomainRoute
          exact
          route="insights-portal-reports-logs-custom-report-exports-list"
          tool="custom-reports-dashboard-tool"
          component={InsightsPortalReportsLogsCustomReportExportsListPage}
          accessControl={insightsPortalDataExplorerAccessControl}
          scopePicker={{
            hiddenScopePicker: true,
          }}
        />
        <DomainRoute
          exact
          route="insights-portal-reports-logs-custom-report-preview"
          tool="custom-reports-dashboard-tool"
          component={InsightsPortalReportsLogsCustomReportPreviewPage}
          accessControl={insightsPortalDataExplorerAccessControl}
          scopePicker={{
            hiddenScopePicker: true,
          }}
        />

        <DomainRoute
          exact
          route="insights-portal-store-labor-performance-v2"
          tool="insights-portal-store-labor-performance-tool"
          component={InsightsPortalStoreLaborPerformanceDashboardPage}
          accessControl={InsightsPortalStoreLaborPerformanceAccessControl}
          // TODO: remove scopePicker once V3 dashboard is fully rolled out
          scopePicker={{
            hiddenScopePicker: true,
            titles: [
              'insightsPortalDomain.dataAndAnalytics.breadcrumb',
              'insightsPortalDomain.storeLaborPerformanceV2.title',
            ],
          }}
        />

        <DomainRoute
          exact
          route="insights-portal-cart-assistant-dashboard"
          tool="cart-assistant-dashboard-tool"
          component={InsightsPortalCartAssistantDashboardPage}
          accessControl={insightsPortalCartAssistantDashboardAccessControl}
        />

        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard"
          tool="caper-dashboard-tool"
          component={InsightsPortalCaperReportingDashboardPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-overview"
          tool="caper-dashboard-tool"
          component={CaperDashboardOverviewPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-sessions"
          tool="caper-dashboard-tool"
          component={CaperDashboardSessionsPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-session-trends"
          tool="caper-dashboard-tool"
          component={CaperDashboardSessionTrendsPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-store-trend"
          tool="caper-dashboard-tool"
          component={CaperDashboardStoreTrendPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-audits"
          tool="caper-dashboard-tool"
          component={CaperDashboardAuditsPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-loyalty"
          tool="caper-dashboard-tool"
          component={CaperDashboardLoyaltyPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-operations"
          tool="caper-dashboard-tool"
          component={CaperDashboardOperationsPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-store-performance"
          tool="caper-dashboard-tool"
          component={CaperDashboardStorePerformancePage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-caper-reporting-dashboard-session-details"
          tool="caper-dashboard-tool"
          component={CaperDashboardSessionDetailsPage}
          accessControl={insightsPortalCaperDashboardAccessControl}
        />

        <DomainRoute
          exact
          route="insights-portal-executive-summary"
          tool="executive-summary-dashboard-tool"
          component={InsightsPortalExecutiveSummaryPage}
        />

        <DomainRoute
          route="insights-portal-overview-ads-dashboard"
          tool="insights-portal-ads-dashboard-tool"
          component={InsightsPortalAdsDashboardPage}
          accessControl={insightsPortalAdsDashboardAccessControl}
        />
        <DomainRoute
          route="insights-portal-sponsored-product-ads-dashboard"
          tool="insights-portal-ads-dashboard-tool"
          component={InsightsPortalSponsoredProductAdsDashboardPage}
          accessControl={insightsPortalAdsDashboardAccessControl}
        />
        <DomainRoute
          route="insights-portal-display-ads-dashboard"
          tool="insights-portal-ads-dashboard-tool"
          component={InsightsPortalDisplayAdsDashboardPage}
          accessControl={insightsPortalAdsDashboardAccessControl}
        />
        <DomainRoute
          route="insights-portal-promotions-ads-dashboard"
          tool="insights-portal-ads-dashboard-tool"
          component={InsightsPortalPromotionsAdsDashboardPage}
          accessControl={insightsPortalAdsDashboardAccessControl}
        />

        <DomainRoute
          exact
          route="insights-portal-display-preview-blocking-tool"
          tool="display-preview-blocking-tool"
          component={InsightsPortalDisplayPreviewBlockingToolPage}
        />

        <DomainRoute
          exact
          route="insights-portal-marketing-flyers-overview"
          tool="insights-portal-flyers-dashboard-tool"
          component={InsightsPortalMarketingFlyersOverviewPage}
          accessControl={insightsPortalFlyersAccessControl}
        />

        <DomainRoute
          exact
          route="insights-portal-marketing-flyers-single-flyer"
          tool="insights-portal-flyers-dashboard-tool"
          component={InsightsPortalmarketingFlyersSingleFlyerPage}
          accessControl={insightsPortalFlyersAccessControl}
        />

        <DomainRoute
          exact
          route="insights-portal-delivery-performance"
          component={InsightsPortalDeliveryPerformancePage}
          accessControl={insightsPortalDeliveryDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-delivery-performance-overview"
          component={DeliveryPerformanceOverviewPage}
          accessControl={insightsPortalDeliveryDashboardAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-delivery-performance-delivery"
          component={DeliveryPerformanceDeliveryPage}
          accessControl={insightsPortalDeliveryDashboardAccessControl}
        />

        <DomainRoute
          exact
          route="insights-portal-offer-ingestion"
          tool="offer-ingestion-dashboard-tool"
          component={InsightsPortalOfferIngestionPage}
          accessControl={insightsPortalOfferIngestionAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-offer-ingestion-summary"
          component={OfferIngestionSummaryPage}
          accessControl={insightsPortalOfferIngestionAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-offer-ingestion-search"
          component={OfferIngestionSearchPage}
          accessControl={insightsPortalOfferIngestionAccessControl}
        />
        <DomainRoute
          exact
          route="insights-portal-offer-ingestion-supported-offers"
          component={OfferIngestionSupportedOffersPage}
          accessControl={insightsPortalOfferIngestionAccessControl}
        />
        {/* @hygen:inject router-route
        injection point for routes, please do not delete */}
      </DashSwitch>
    </InsightsPortalFilterParamsProvider>
  </DomainRoute>
)
